import { View } from 'backbone';
import { Consola } from 'consola';
import $ from 'jquery';
import _ from 'underscore';
import VideoHtml from '../html/engine/videoScreen.html';
import Environment from './environment';
import { getLogger } from './logging/logger';
import AudioManager from './manager/audioManager';
import gsap from './util/hwpTVGSAP';
import { pruneObject, wait } from './util/utilities';

function isValidGsapTarget(target) {
	return !!target && Array.isArray(target) ? target.some((item) => !!item) : true;
}

/**
 * @typedef {object} BaseViewOptions
 * @property {string} [id] Element id attribute
 * @property {string} [className] Element class name attribute
 * @property {object[]} [pages] Routes accessible from instance
 */

/**
 * @property {string | () => string} [id]
 * @property {string | () => string} className
 * @property {Backbone.EventsHash | () => Backbone.EventsHash} events
 * @property {() => function} [template]
 */
export default class BaseView extends View {
	get CN() {
		return BaseView.name;
	}

	/** @type {Consola} */
	get logger() {
		return getLogger(this.CN);
	}

	id() {
		return undefined;
	}

	className() {
		return undefined;
	}

	/** @type {_.CompiledTemplate} */
	get template() {
		return undefined;
	}

	get videoTemplate() {
		return _.template(VideoHtml);
	}

	/**
	 * @param {BaseViewOptions} [options] Data for the view
	 */
	constructor(options) {
		super(options);

		this.logger.log('[constructor]', { options });
	}

	/**
	 * @param {BaseViewOptions} options
	 */
	initialize(options) {
		this.logger.log(`[${this.initialize.name}]`, { options });

		this.options = { ...options };
		Object.assign(this, options);
	}

	/**
	 * Renders a template with the options provided when constructing
	 * the view.
	 */
	render() {
		this.logger.log(`[${this.render.name}]`);

		if (this.template) {
			this.$el.empty().append(this.template({ ...this.options }));
		}
	}

	/**
	 * Dismisses a DOM node. If a string is provided as an element selector, the lookup of that
	 * selector will be limited to the views DOM tree.
	 * @param {string|HTMLElement|HTMLElement[]|JQuery} target Some reference to the element to dismiss.
	 * @param {object} [props] Object to be given to GSAP with provided tweening properties.
	 * @returns {GSAPTimeline}
	 */
	dismissElement(target, props) {
		this.logger.log(`[${this.dismissElement.name}]`, { target, props });

		if (typeof target === 'string') {
			target = this.$(target).toArray();
		}

		if (!isValidGsapTarget(target)) {
			this.logger.warn('Invalid GSAP targets');
			return gsap.timeline();
		}

		return gsap.timeline().fadeOut(target, pruneObject({ ...props }));
	}

	/**
	 * Displays a DOM node. If a string is provided as an element selector, the lookup of that
	 * selector will be limited to the views DOM tree.
	 * @param {string|HTMLElement|HTMLElement[]|JQuery} target Some reference to the element to display.
	 * @param {object} [props] Object to be given to GSAP with provided tweening properties.
	 * @returns {GSAPTimeline}
	 */
	displayElement(target, props) {
		this.logger.log(`[${this.displayElement.name}]`, { target, props });

		if (typeof target === 'string') {
			target = this.$(target).toArray();
		}

		if (!isValidGsapTarget(target)) {
			this.logger.warn('Invalid GSAP targets');
			return gsap.timeline();
		}

		return gsap.timeline().fadeIn(target, pruneObject({ ...props }));
	}

	/**
	 * Replaces a DOM node's descendent tree with another one by dismissing the target, replacing
	 * it's children, and displaying it again. If a string is provided as an element selector, the
	 * lookup of that selector will be limited to the views DOM tree.
	 * @param {string | HTMLElement | JQuery} target Some reference to the element to dismiss.
	 * @param {string | HTMLElement | JQuery} view The view to be inserted into the DOM to replace the
	 * targets chidlren.
	 * @param {function} [onReplaced] A callback function that will be called after the DOM has been
	 * updated, but before the elements are displayed.
	 * @param {object} [props] Object to be given to GSAP with provided tweening properties.
	 * @returns {Promise}
	 */
	replaceElement(target, view, onReplaced, props) {
		this.logger.log(`[${this.replaceElement.name}]`, { target, view, props });

		return this.dismissElement(target, props)
			.then(() => {
				const childVideos = this.$(target).find('video');
				if (childVideos.length) {
					// Clean up any videos
					this.cleanUpVideo(childVideos);
				}

				const $element = _.isString(target) ? this.$(target) : $(target);
				$element.empty().append(view);
			})
			.then(() => onReplaced?.())
			.then(() => this.displayElement(target, props));
	}

	/**
	 * Dismisses the current views DOM node.
	 * @param {object} [props] Object to be given to GSAP with provided tweening properties.
	 * @returns {GSAPTimeline}
	 */
	dismissView(props) {
		this.logger.log(`[${this.dismissView.name}]`, { props });

		return this.dismissElement(this.el, props);
	}

	/**
	 * Displays the current views DOM node.
	 * @param {object} [props] Object to be given to GSAP with provided tweening properties.
	 * @returns {GSAPTimeline}
	 */
	displayView(props) {
		this.logger.log(`[${this.displayView.name}]`, { props });

		return this.displayElement(this.el, props);
	}

	/**
	 * Replaces the current views DOM node descendent tree with another one by dismissing the target,
	 * replacing it's children, and displaying it again.
	 * @param {string | HTMLElement | JQuery} view The view to be inserted into the DOM to replace the
	 * targets chidlren.
	 * @param {function} [onReplaced] A callback function that will be called after the DOM has been
	 * updated, but before the elements are displayed.
	 * @param {object} [props] Object to be given to GSAP with provided tweening properties.
	 * both dismiss and display durations.
	 * @returns {Promise}
	 */
	replaceView(view, onReplaced, props) {
		this.logger.log(`[${this.replaceView.name}]`, { view, onReplaced, props });

		return this.replaceElement(this.el, view, onReplaced, props);
	}

	destroy() {
		this.logger.log(`[${this.destroy.name}]`);

		this.$el.clearQueue().stop();
		this.cleanUpVideo();
		this.$el.empty();
		this.undelegateEvents();
		this.off();
	}

	/**
	 * Replaces the container with a video from the provided data.
	 * @param {object} videoData Object containing 'video', 'videoImage', and 'videoCaption'.
	 * @param {string | HTMLElement | JQuery} container element to replaced with a video
	 */
	displayVideo(videoData, container = this.el, showControls = false, onDisplayed = undefined) {
		this.logger.log(`[${this.displayVideo.name}]`, { videoData, container, showControls });

		if (!videoData.video) {
			return Promise.reject(new Error('No video URL provided.'));
		}

		const videoScreen = this.videoTemplate(videoData);
		return this.replaceElement(container, videoScreen, () => {
			/** @type {JQuery<HTMLVideoElement>} */
			const $video = this.$('video');
			const video = $video.get(0);
			if (video) {
				$video.one('canplaythrough', () => {
					this.logger.debug('[video][canplaythrough]');
					AudioManager.stopAllSounds();
					this.playVideo(video, showControls);

					if (Environment.isSetTopBox()) {
						/**
						 * Wait for time update, then wait for 1/4 second before locking video size.
						 * This prevents the video from 'jumping' between sizes on STB
						 */
						$video.one('timeupdate', () =>
							this.wait(250).then(() => this.lockVideoSize()),
						);
					}
				});

				$video.get(0).load(); // Required for iOS Safari
			} else {
				this.logger.error('[Video not found in DOM]');
			}

			onDisplayed?.();
		}).then(() => new Promise((resolve) => this.$('video').one('ended', resolve)));
	}

	playVideo(video, allowControls) {
		this.logger.log(`[${this.playVideo.name}]`);

		let vid = video;

		if (!(vid instanceof $)) {
			vid = this.$(vid);
		}

		if (allowControls) {
			vid.attr('controls', true);
		} else {
			// Create timeout to activate controls
			const controlsTimeout = setTimeout(() => {
				if (vid.get(0).paused) {
					/** Video is still paused. This could be a situation that needs the controls activated to start playback. */
					vid.attr('controls', true);
				}
			}, 500);

			// listen for timeupdate to clear the controls timeout
			vid.one('play', () => {
				/** Video playback succesful. Don't worry about activating controls to start playback.  */
				vid.removeAttr('controls');
				clearTimeout(controlsTimeout);
			});
		}

		// Prep audio pause and resume
		AudioManager.pauseBackgroundMusic();
		vid.on('ended', () => AudioManager.resumeBackgroundMusic());

		// play video
		return vid.get(0).play();
	}

	lockVideoSize() {
		this.logger.log(`[${this.lockVideoSize.name}]`);

		/** @type {JQuery<HTMLVideoElement>} */
		const vid = this.$('video');
		const width = vid.width();
		const height = vid.height();

		if (width === 0 || height === 0) {
			this.wait(100).then(this.lockVideoSize.bind(this));
			return;
		}

		const { videoWidth, videoHeight } = vid[0];
		const videoAspectRatio = videoWidth / videoHeight;
		const containerAspectRatio = width / height;

		if (containerAspectRatio > videoAspectRatio) {
			// Container is wider than video, constrain width
			vid.width(height * videoAspectRatio);
			vid.height(height);
		} else if (containerAspectRatio < videoAspectRatio) {
			// Container is taller than video, constrain height
			vid.width(width);
			vid.height(width / videoAspectRatio);
		} else {
			vid.width(width);
			vid.height(height);
		}
	}

	/**
	 * Handles tearing down a video. Takes steps to properly unload video resources on low power devices.
	 * @param {string | HTMLVideoElement} target
	 */
	cleanUpVideo(target = 'video') {
		this.logger.log(`[${this.cleanUpVideo.name}]`, { video: target });

		this.$(target)
			.toArray()
			.forEach((/** @type {HTMLVideoElement} */ video) => {
				if (_.isElement(video) && video.tagName === 'VIDEO') {
					video.pause();
					video.setAttribute('src', '');
					video.load();
				}
			});
	}

	wait(msToWait) {
		this.logger.log(`[${this.wait.name}]`, { msToWait });

		return wait(msToWait);
	}
}
